import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import './MenuGroupChart.css';
import '../charts/ChartStyles.css';

interface MenuGroup {
  menu_group: string;
  net_sales: number;
  quantity: number;
  percentage: number;
}

interface MenuCategory {
  menu_category: string;
  net_sales: number;
  quantity: number;
  percentage: number;
}

interface MenuCategoryWithGroup extends MenuCategory {
  menu_group: string;
  recalculated_percentage: number;
}

const MenuGroupChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { isFiltersApplying } = useFilters();

  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  
  const menuGroups = (dashboardData?.chartData as any)?.menuGroups || [];
  const menuCategories = (dashboardData?.chartData as any)?.menuCategories || [];
  
  const menuGroupColors = [
    '#F97F76', '#474646', '#F9C27B', '#4285F4', '#34A853', '#EA4335',
    '#9C27B0', '#FF9800', '#795548', '#607D8B', '#E91E63', '#00BCD4',
    '#8BC34A', '#FFC107', '#673AB7', '#009688'
  ];

  
  const isLaptop = window.innerWidth <= 1600;
  const isIPad = window.innerWidth <= 1024;
  const isIPhone = window.innerWidth <= 480;

  
  const extractNumber = (value: string | number): number => {
    if (typeof value === 'string') {
      return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
    }
    return value || 0;
  };

  
  const getTextColor = (hexColor: string): string => {
    
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
    
    
    return brightness > 140 ? '#000000' : '#ffffff';
  };

  

  const handleChartEvents = {
    'click': function(params: any) {
      if (params.seriesType === 'pie') {
        if (params.name === selectedGroup) {
          setSelectedGroup(null);
        } else {
          setSelectedGroup(params.name);
        }
      }
    }
  };

  const getOption = () => {
    if (!menuGroups || menuGroups.length === 0) {
      return {};
    }
    
    const pieData = menuGroups.map((group: MenuGroup, index: number) => ({
      name: group.menu_group,
      value: extractNumber(group.net_sales),
      percentage: extractNumber(group.percentage),
      quantity: extractNumber(group.quantity)
    }));
    
    
    let barData = selectedGroup 
      ? menuCategories.filter((cat: any) => cat.menu_group === selectedGroup)
      : menuCategories;
    
    
    barData = barData.sort((a: any, b: any) => b.net_sales - a.net_sales);
    
    
    const totalSales = barData.reduce((sum: number, cat: any) => sum + cat.net_sales, 0);
    barData = barData.map((cat: any) => ({
      ...cat,
      recalculated_percentage: totalSales > 0 ? (cat.net_sales / totalSales) * 100 : 0
    }));
    
    const getGroupColor = (index: number): string => {
      if (selectedGroup && menuGroups) {
        const groupIndex = menuGroups.findIndex((g: MenuGroup) => g.menu_group === selectedGroup);
        return menuGroupColors[groupIndex % menuGroupColors.length];
      } else {
        const categoryItem = barData[index];
        if (categoryItem && menuGroups) {
          const groupIndex = menuGroups.findIndex((g: MenuGroup) => g.menu_group === categoryItem.menu_group);
          return menuGroupColors[groupIndex % menuGroupColors.length];
        }
        return menuGroupColors[index % menuGroupColors.length];
      }
    };
    
    const categories = barData.map((item: any) => item.menu_category);
    const values = barData.map((item: any) => extractNumber(item.net_sales));
    const percentages = barData.map((item: any) => item.recalculated_percentage);
    const quantities = barData.map((item: any) => extractNumber(item.quantity));


    return {
      grid: [
        {
          left: '2%',
          right: '58%',
          top: isIPhone ? '15%' : (isIPad ? '16%' : (isLaptop ? '18%' : '20%')),
          bottom: '8%',
          containLabel: false,
          show: false,
          borderWidth: 0
        },
        {
          left: '47%',
          right: '5%',
          top: isIPhone ? '4%' : (isIPad ? '5%' : (isLaptop ? '6%' : '8%')),
          bottom: '5%',
          containLabel: true,
          show: false,
          borderWidth: 0
        }
      ],
      legend: {
        data: pieData.map((item: any, index: number) => ({
          name: item.name,
          textStyle: {
            color: selectedGroup === item.name 
              ? menuGroupColors[index % menuGroupColors.length]
              : (selectedGroup ? '#999' : '#333'),
            fontWeight: selectedGroup === item.name ? 'bold' : 'bold'
          }
        })),
        top: isIPhone ? '2%' : (isIPad ? '3%' : '4%'),
        left: '4%',
        width: '50%',
        textStyle: {
          fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 13)),
          fontWeight: 'bold'
        },
        itemGap: isIPhone ? 6 : (isIPad ? 7 : (isLaptop ? 8 : 10)),
        itemWidth: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 14)),
        itemHeight: isIPhone ? 6 : (isIPad ? 7 : (isLaptop ? 8 : 10)),
        orient: 'horizontal',
        selector: false,
        emphasis: {
          textStyle: {
            fontWeight: 'bold'
          }
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          if (params.seriesType === 'pie') {
            const group = menuGroups?.find((g: MenuGroup) => g.menu_group === params.name);
            return `<div style="padding: 3px; border-left: 4px solid ${params.color};">
              <div style="font-weight: bold; margin-bottom: ${isIPhone ? '4px' : (isIPad ? '5px' : (isLaptop ? '6px' : '8px'))}; font-size: ${isIPhone ? '11px' : (isIPad ? '12px' : (isLaptop ? '13px' : '14px'))};">${params.name}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '3px' : (isIPad ? '4px' : '5px')};">
                <span style="margin-right: 15px;">${t('menuChart.sales')}:</span>
                <span style="font-weight: bold;">$${params.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '3px' : (isIPad ? '4px' : '5px')};">
                <span style="margin-right: 15px;">${t('menuChart.quantity')}:</span>
                <span style="font-weight: bold;">${extractNumber(group?.quantity || 0).toLocaleString()}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('menuChart.percentage')}:</span>
                <span style="font-weight: bold;">${extractNumber(group?.percentage || 0).toFixed(2)}%</span>
              </div>
            </div>`;
          }
          
          if (params.seriesType === 'bar') {
            const idx = params.dataIndex;
            const borderColor = getGroupColor(idx);
            const categoryItem = barData[idx] as MenuCategoryWithGroup;
            
            return `<div style="padding: 3px; border-left: 4px solid ${borderColor};">
              <div style="font-weight: bold; margin-bottom: ${isIPhone ? '4px' : (isIPad ? '5px' : (isLaptop ? '6px' : '8px'))}; font-size: ${isIPhone ? '11px' : (isIPad ? '12px' : (isLaptop ? '13px' : '14px'))};">${categories[idx]}</div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '3px' : (isIPad ? '4px' : '5px')};">
                <span style="margin-right: 15px;">${t('menuChart.group')}:</span>
                <span style="font-weight: bold;">${categoryItem.menu_group}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '3px' : (isIPad ? '4px' : '5px')};">
                <span style="margin-right: 15px;">${t('menuChart.sales')}:</span>
                <span style="font-weight: bold;">$${extractNumber(values[idx]).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: ${isIPhone ? '3px' : (isIPad ? '4px' : '5px')};">
                <span style="margin-right: 15px;">${t('menuChart.quantity')}:</span>
                <span style="font-weight: bold;">${extractNumber(quantities[idx]).toLocaleString()}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span style="margin-right: 15px;">${t('menuChart.percentage')}:</span>
                <span style="font-weight: bold;">${percentages[idx].toFixed(2)}%</span>
              </div>
            </div>`;
          }
          
          return '';
        }
      },
      series: [
        {
          name: '메뉴 그룹',
          type: 'pie',
          radius: isIPhone ? '45%' : (isIPad ? '55%' : (isLaptop ? '55%' : '70%')),
          center: ['23%', '55%'],
          avoidLabelOverlap: false,
          selectedMode: 'single',
          itemStyle: {
            borderWidth: isIPhone ? 1 : (isIPad ? 1.2 : (isLaptop ? 1.5 : 2)),
            borderColor: '#fff'
          },
          label: {
            show: true,
            position: 'inside',
            formatter: function(params: any) {
              
              if (params.percent >= 10) {
                return `${params.name}\n${params.percent.toFixed(1)}%`;
              }
              return '';
            },
            fontSize: isIPhone ? 9 : (isIPad ? 10 : (isLaptop ? 11 : 12)),
            fontWeight: 'bold',
            color: function(params: any) {
              
              const colorIndex = pieData.findIndex((item: any) => item.name === params.name);
              const bgColor = menuGroupColors[colorIndex % menuGroupColors.length];
              return getTextColor(bgColor);
            }
          },
          labelLine: {
            show: false
          },
          emphasis: {
            itemStyle: {
              shadowBlur: isIPhone ? 8 : (isIPad ? 10 : (isLaptop ? 10 : 15)),
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.4)',
              scale: true,
              scaleSize: isIPhone ? 1 : (isIPad ? 2 : (isLaptop ? 2 : 5))
            }
          },
          data: pieData.map((item: any, index: number) => {
            const bgColor = menuGroupColors[index % menuGroupColors.length];
            return {
              ...item,
              itemStyle: {
                color: bgColor,
                opacity: selectedGroup && selectedGroup !== item.name ? 0.25 : 1
              },
              label: {
                color: getTextColor(bgColor)
              },
              emphasis: {
                itemStyle: {
                  color: bgColor,
                  opacity: 1,
                  shadowBlur: isIPhone ? 12 : (isIPad ? 15 : (isLaptop ? 15 : 20)),
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            };
          })
        },
        {
          name: '메뉴 카테고리',
          type: 'bar',
          barWidth: isIPhone ? '45%' : (isIPad ? '50%' : (isLaptop ? '55%' : '60%')),
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: values.map((value: any, index: number) => ({
            value: value,
            itemStyle: {
              color: getGroupColor(index)
            }
          })),
          label: {
            show: barData.length > 0,
            position: 'right',
            formatter: function(params: any) {
              const idx = params.dataIndex;
              const value = extractNumber(params.value);
              const maxValue = Math.max(...values.map((v: any) => extractNumber(v)));
              const formattedValue = `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
              const percentage = percentages[idx].toFixed(1);
              
              const shouldBreakLine = maxValue > 0 && (value / maxValue) > 0.6;
              
              if (shouldBreakLine) {
                return `${formattedValue}\n(${percentage}%)`;
              } else {
                return `${formattedValue} (${percentage}%)`;
              }
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 8 : (isIPad ? 9 : (isLaptop ? 9 : 10)),
            color: '#333'
          }
        }
      ],
      xAxis: [
        { type: 'value', gridIndex: 0, show: false },
        { 
          type: 'value', 
          gridIndex: 1,
          show: false
        }
      ],
      yAxis: [
        { type: 'category', gridIndex: 0, show: false },
        { 
          type: 'category', 
          gridIndex: 1, 
          data: categories,
          axisLabel: {
            interval: 0,
            formatter: function(value: string) {
              if (value.length > 20) {
                const words = value.split(' ');
                if (words.length > 1) {
                  const mid = Math.ceil(words.length / 2);
                  return words.slice(0, mid).join(' ') + '\n' + words.slice(mid).join(' ');
                } else {
                  return value.substring(0, 10) + '\n' + value.substring(10);
                }
              }
              return value;
            },
            fontWeight: 'bold',
            fontSize: isIPhone ? 8 : (isIPad ? 9 : (isLaptop ? 9 : 11)),
            color: '#333',
            lineHeight: isIPhone ? 8 : (isIPad ? 9 : (isLaptop ? 9 : 12))
          }
        }
      ]
    };
  };

  

  if (loading || isFiltersApplying) {
    return (
      <div className="menu-group-chart">
        <div className="menu-group-title-band">
          <h2 style={{
            fontSize: isIPhone ? '14px' : (isIPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isIPhone ? '6px 15px' : (isIPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>
            {t('menuChart.title')}
          </h2>
        </div>
        <div className="menu-group-chart-container chart-container">
          <div className="menu-group-loading">
            <div className="menu-group-loading-spinner"></div>
            <div className="menu-group-loading-text">
              {isFiltersApplying ? t('menuChart.filterApplying') : t('menuChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="menu-group-chart">
        <div className="menu-group-title-band">
          <h2 style={{
            fontSize: isIPhone ? '14px' : (isIPad ? '15px' : (isLaptop ? '16px' : '18px')),
            padding: isIPhone ? '6px 15px' : (isIPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
          }}>
            {t('menuChart.title')}
          </h2>
        </div>
        <div className="menu-group-chart-container">
          <div className="menu-group-error">
            <div className="menu-group-error-icon">⚠️</div>
            <div className="menu-group-error-text">{typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-group-chart">
      {isFiltersApplying && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>{t('menuChart.filterApplying')}</span>
        </div>
      )}
      
      <div className="menu-group-title-band">
        <h2 style={{
          fontSize: isIPhone ? '14px' : (isIPad ? '15px' : (isLaptop ? '16px' : '18px')),
          padding: isIPhone ? '6px 15px' : (isIPad ? '7px 18px' : (isLaptop ? '8px 20px' : '10px 25px'))
        }}>
          {t('menuChart.title')}
        </h2>
      </div>
      <div className="menu-group-chart-container">
        {selectedGroup && (
          <div className="menu-group-filter-info">
            <button 
              className="menu-group-reset-button" 
              onClick={() => setSelectedGroup(null)}
              disabled={isFiltersApplying}
              style={{
                fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '11px' : '13px')),
                padding: isIPhone ? '3px 8px' : (isIPad ? '4px 10px' : (isLaptop ? '4px 12px' : '5px 15px'))
              }}
            >
              {t('menuChart.viewAll')}
            </button>
          </div>
        )}
        <ReactECharts 
          option={getOption()} 
          style={{ 
            height: isIPhone ? '100%' : (isIPad ? '420px' : (isLaptop ? '380px' : '460px')), 
            width: '100%' 
          }}
          className="menu-group-echarts"
          opts={{ renderer: 'canvas' }}
          onEvents={handleChartEvents}
        />
      </div>
    </div>
  );
};

export default MenuGroupChart;